// articles/ScoringYourRounds.js
// "Scoring Your Rounds" (Sept 27, 2026). Second card in the 📖 Guides chooser,
// after Getting Started. Scoring only — Dennis, Sept 27: games are live for two
// complimentary orgs only and their plan level isn't decided, so games get their
// own guide later, once there are more of them. This guide sells and explains
// scoring: the three ways to enter scores, the arithmetic TTO does, GPS yardage,
// the live leaderboard, celebrations, checking cards, posting to GHIN, scorecards
// and reports, and the stats. Uses the shared guide parts (step list, print
// layout, Close bar). Labels are quoted from the live screens (App.js LiveScoring,
// PostRoundScoring, components/scoring/ScoringStats.jsx); if one changes, change
// it here too.

import React from 'react';
import { colors, getGuideBySlug } from '../guidesData';
import { H2, H3, P, UL, LI, InlineLink, Callout, GuideCTA, FAQ } from '../articleUI';
import { Step, Btn, Heads, StepTracker, PhoneShot, PhoneRow, PRINT_CSS } from './eventGuideParts';

const guide = getGuideBySlug('scoring-your-golf-rounds');

const FAQS = [
  {
    q: 'Does every player need a smartphone?',
    a: 'No. One player can keep the card for up to three others with + Score for another player, players can enter their round afterwards, or you can type in paper cards yourself in Post-Round Score Entry.',
  },
  {
    q: 'What if there’s no signal on the course?',
    a: 'Keep scoring. Scores are kept on the phone and saved to TTO as soon as the connection comes back; a small dot in the header shows whether the phone is online.',
  },
  {
    q: 'Why don’t I see yardage to the green?',
    a: 'Yardage shows only on courses where TTO has confirmed the location of every green, and only if the player allows the app to use his location. Otherwise the card shows the hole’s yardage from the tee.',
  },
  {
    q: 'Do total-score rounds count in my stats?',
    a: 'Yes, for scoring average, putts per round and the group and course pages. Hole-by-hole stats (par-3/4/5 averages, score distribution, greens in regulation) need hole-by-hole scores.',
  },
  {
    q: 'Does TTO post my score to GHIN for me?',
    a: 'No. Each player posts his own score, with the Post to GHIN button on his card or on ghin.com. TTO never posts on a player’s behalf.',
  },
  {
    q: 'Can I fix a score after the round?',
    a: 'The player can until he posts to GHIN. You can at any time in Admin → ⛳ Scoring → Post-Round Score Entry (rounds from the last 14 days). A score already posted to GHIN is corrected on ghin.com.',
  },
];

export default function ScoringYourRounds() {
  return (
    <article>
      <h1
        style={{
          fontFamily: "'DM Serif Display', Georgia, serif",
          fontSize: 38,
          fontWeight: 400,
          color: colors.gray900,
          margin: '0 0 8px',
          lineHeight: 1.15,
        }}
      >
        Scoring Your Rounds
      </h1>
      <style>{PRINT_CSS}</style>
      <div
        style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', gap: 12, flexWrap: 'wrap', margin: '0 0 28px' }}
      >
        <p style={{ fontSize: 15, color: colors.gray400, margin: 0 }}>
          {guide.readMinutes} min read &middot; Scores, stats and records
        </p>
        <button
          type="button"
          className="guide-no-print"
          onClick={() => window.print()}
          style={{
            background: 'white',
            color: colors.green800,
            border: `1px solid ${colors.gray300}`,
            borderRadius: 8,
            padding: '8px 14px',
            fontSize: 14,
            fontWeight: 700,
            cursor: 'pointer',
          }}
        >
          🖨️ Print this guide
        </button>
      </div>

      <P>
        Once your tee time is set up (see{' '}
        <InlineLink to="/guides/getting-started-with-tee-time-organizer">Getting Started</InlineLink>
        ), your players can keep score on their phones. TTO adds up the card, works out handicaps and
        net scores, shows a live leaderboard, and builds each player&rsquo;s stats round after round.
        This guide explains how players score, what you do as organizer, and what everyone gets out
        of it.
      </P>

      <Callout>
        <strong>Why score in TTO:</strong>
        <UL>
          <LI>
            <strong>Easy:</strong> one tap enters par; plus and minus for anything else. One player
            can keep the card for his whole group.
          </LI>
          <LI>
            <strong>No arithmetic:</strong> totals, score to par, course handicap, stroke holes, net
            score and the GHIN adjusted score are worked out as you go.
          </LI>
          <LI>
            <strong>Yards to the green:</strong> on courses with mapped greens, the card shows your
            distance to the center of the green.
          </LI>
          <LI>
            <strong>A live leaderboard</strong> and celebrations when someone in the group makes a
            birdie or better.
          </LI>
          <LI>
            <strong>Records that build themselves:</strong> scorecards, reports, a Wall of Fame and
            detailed personal stats.
          </LI>
        </UL>
      </Callout>

      <StepTracker
        storageKey="tto_guide_scoring_steps"
        steps={[
          'Ready to score (once)',
          'Choose how to enter scores',
          'Scoring a live round',
          'Leaderboard and celebrations',
          'Check the cards',
          'Post to GHIN',
          'Scorecards and reports',
          'Stats',
        ]}
        note="Step 1 is done once by the organizer. Steps 2 to 6 happen on the day; 7 and 8 are there whenever you want them. Tap a step to jump to it."
      />

      <Step n={1} title="Get ready to score" when="Once, before your first scored round">
        <UL>
          <LI>
            <strong>Scoring is included from the Starter plan up.</strong> You&rsquo;ll find{' '}
            <strong>⛳ Scoring</strong> under <Btn>⚙️ Admin</Btn>, and players see{' '}
            <strong>⛳ Scoring</strong> in their app. If it&rsquo;s missing, or shows 🔒, contact us
            to have scoring turned on for your group.
          </LI>
          <LI>
            <strong>Each course needs its hole data:</strong> par, yardage and hole handicap for every
            hole, on each tee (<Btn>⚙️ Admin</Btn> &rarr; <strong>Courses</strong>). Importing the
            course from the course database, or with <Btn>📷 Import from Scorecard</Btn>, fills it in.
          </LI>
          <LI>
            <strong>Each player needs a Handicap Index and a preferred tee</strong>, so his course
            handicap and net score can be determined.
          </LI>
          <LI>
            <strong>Celebrations:</strong> in <Btn>⚙️ Admin</Btn> &rarr; <strong>⛳ Scoring</strong>,
            set <strong>🎉 In-round score celebrations</strong> to Birdies and better, Eagles and
            better, Albatross and better, or Hole-in-one only. It is Off until you choose (see step 4).
          </LI>
        </UL>
      </Step>

      <Step n={2} title="Choose how to enter scores" when="Each player, each round">
        <P>
          From the reminder email, a player opens his app and taps <strong>⛳ Scoring</strong>. TTO
          asks <strong>How would you like to enter scores?</strong>
        </P>
        <UL>
          <LI>
            <strong>📱 Live Round:</strong> score hole by hole on the course. This gives the most:
            yardage, the live leaderboard, celebrations and full stats.
          </LI>
          <LI>
            <strong>📝 Post-Round Entry — Hole by Hole:</strong> enter the whole card after the round,
            with scores and putts for each hole. Full stats.
          </LI>
          <LI>
            <strong>🔢 Post-Round Entry — Total Score:</strong> just the front 9, back 9 and total
            putts. Quickest, and it still counts toward scoring average and putts, but not the
            hole-by-hole stats.
          </LI>
        </UL>
      </Step>

      <Step n={3} title="Scoring a live round" when="On the course">
        <H3>The card</H3>
        <UL>
          <LI>
            The header shows the course, tee, par and the player&rsquo;s course handicap (CH). Each
            hole shows its par, yardage and hole handicap.
          </LI>
          <LI>
            <strong>Score:</strong> the box shows par in grey; one tap enters it, then{' '}
            <strong>−</strong> and <strong>+</strong> adjust. TTO labels the result: birdie, par, bogey
            and so on.
          </LI>
          <LI>
            <strong>Putts:</strong> optional, but enter them for putting stats.
          </LI>
          <LI>
            The bar across the top keeps the running totals: <strong>THRU</strong>,{' '}
            <strong>GROSS</strong>, <strong>ADJ</strong>, <strong>PUTTS</strong> and{' '}
            <strong>VS PAR</strong>.
          </LI>
          <LI>
            Move with <strong>Hole N →</strong> and <strong>← Hole N</strong>, or tap any hole in the row
            of hole numbers. Scores save as you go.
          </LI>
        </UL>
        <PhoneShot
          src="/guides/scoring/card.webp"
          alt="The live scorecard on the 7th hole: par 3, 178 yards, Yards to Center 171, the score and putts boxes, and running totals across the top"
          caption="The live card on the 7th tee: yards to the green, par, yardage and hole handicap, with the running totals across the top."
        />
        <H3>The arithmetic TTO does for you</H3>
        <UL>
          <LI>
            <strong>Course handicap</strong> from the player&rsquo;s Handicap Index and the tee he plays.
          </LI>
          <LI>
            <strong>Stroke holes:</strong> a green asterisk marks every hole where he gets a stroke.
          </LI>
          <LI>
            <strong>ADJ</strong> is his adjusted score for GHIN: any hole worse than a net double bogey
            is counted as a net double bogey, and the card shows &ldquo;Adj&rdquo; in red on that hole.
          </LI>
        </UL>
        <H3>Yards to the green</H3>
        <UL>
          <LI>
            On courses where TTO has mapped every green, the card shows{' '}
            <strong>Yards to Center</strong> from the player&rsquo;s position, using the phone&rsquo;s
            GPS. The phone asks once for permission to use his location.
          </LI>
          <LI>
            It shows &ldquo;GPS settling&rdquo; until the position is accurate, so it never shows a
            wrong number. On other courses the card shows the hole&rsquo;s yardage from the tee.
          </LI>
        </UL>
        <H3>Scoring for others, and on the go</H3>
        <UL>
          <LI>
            <strong>+ Score for another player</strong> lets one phone keep the card for up to three
            others. Those players can open their own app and follow along.
          </LI>
          <LI>
            <strong>Picked up?</strong> Tap <strong>X</strong>. It counts as a net double bogey and
            shows as &ldquo;7x&rdquo;. TTO won&rsquo;t move to the next hole until every player on the
            card has a score or an X.
          </LI>
          <LI>
            <strong>No signal?</strong> Keep scoring. Scores are kept on the phone and saved as soon as
            the connection returns.
          </LI>
        </UL>
      </Step>

      <Step n={4} title="The leaderboard and celebrations" when="During the round">
        <H3>Live leaderboard</H3>
        <UL>
          <LI>
            The <strong>📊 Leaderboard</strong> tab on the card ranks the field, gross or net, with
            each player&rsquo;s score to par and how many holes he has played. Tap a player to see his
            card.
          </LI>
          <LI>
            <strong>For spectators:</strong> in <Btn>⚙️ Admin</Btn> &rarr; <strong>⛳ Scoring</strong>{' '}
            &rarr; <strong>📊 Live Leaderboard</strong>, <Btn>🔗 Share this leaderboard</Btn> gives a
            link anyone can open without signing in, and a <strong>📺 TV view</strong> for the
            clubhouse. Adding the leaderboard link to your reminder message shares it automatically.
          </LI>
        </UL>
        <H3>Celebrations</H3>
        <UL>
          <LI>
            When anyone in the tee time scores at the level you chose (step 1), everyone in that tee
            time who has the live card open sees a banner, for example{' '}
            <strong>&ldquo;🐦 Tom Reilly just had a birdie on #5&rdquo;</strong>. A hole-in-one is always
            celebrated.
          </LI>
          <LI>
            The <strong>🎉</strong> tab on the card lists every celebration so far this round.
          </LI>
        </UL>
        <PhoneRow>
          <PhoneShot
            src="/guides/scoring/board.webp"
            alt="The live leaderboard: players ranked by gross score with holes played, course handicap and net score"
            caption="The live leaderboard, gross or net."
            width={230}
          />
          <PhoneShot
            src="/guides/scoring/celebrate.webp"
            alt="A celebration banner at the top of the card: Tom Reilly just had a birdie on #5"
            caption="A celebration banner, and the 🎉 tab with its count."
            width={230}
          />
        </PhoneRow>
      </Step>

      <Step n={5} title="Check the cards" when="After the round (organizer)">
        <UL>
          <LI>
            <Btn>⚙️ Admin</Btn> &rarr; <strong>⛳ Scoring</strong> &rarr;{' '}
            <strong>⛳ Post-Round Score Entry</strong>, then choose the round (rounds from the last 14
            days are listed). Each player has a tab, by last name.
          </LI>
          <LI>
            Compare with the signed card and correct any hole or putts, or type X for a pick-up. Then{' '}
            <Btn>Save [player]&rsquo;s Scores</Btn>, or <Btn>Save All Scores</Btn> after several.
          </LI>
          <LI>
            <strong>Paper cards:</strong> enter a player&rsquo;s whole card the same way.
          </LI>
          <LI>
            A blank hole <em>between</em> holes he played won&rsquo;t save: enter a score or an X.
          </LI>
        </UL>
      </Step>

      <Step n={6} title="Post to GHIN" when="After the round (each player)">
        <UL>
          <LI>
            <strong>Each player posts his own score.</strong> TTO never posts for him.
          </LI>
          <LI>
            Once 9 or more holes are scored and saved, <Btn>Post to GHIN</Btn> appears on his card. He
            checks the course and tee and confirms; hole-by-hole rounds are posted hole by hole. The
            card then shows <strong>🔒 GHIN Posted</strong> and the scores lock.
          </LI>
          <LI>
            To post from TTO, the player first connects his GHIN account in his Profile. Posting to
            GHIN is included with the Starter plan and up. He can always post on ghin.com instead.
          </LI>
          <LI>
            The <strong>GHIN posting reminder</strong> email reminds players after the round, and skips
            anyone whose score is already posted.
          </LI>
        </UL>
      </Step>

      <Step n={7} title="Scorecards, reports and the Wall of Fame" when="Any time">
        <UL>
          <LI>
            <strong>⛳ Scorecard Report</strong> (<Btn>⚙️ Admin</Btn> &rarr; <strong>Reports</strong>):
            every player&rsquo;s gross, net and putts for a round, ready to print or email to the
            players.
          </LI>
          <LI>
            <strong>A player&rsquo;s card:</strong> tap any name on a leaderboard to see his hole-by-hole
            gross and net.
          </LI>
          <LI>
            <strong>🏆 Wall of Fame:</strong> TTO records aces, albatrosses, eagles and par-or-better
            rounds automatically as scores are saved. Players see it in their stats; you can hide an
            entry or correct a name in <Btn>⚙️ Admin</Btn> &rarr; <strong>⛳ Scoring</strong>.
          </LI>
          <LI>
            <strong>🏌️ My Rounds:</strong> players can also log and score rounds they play outside
            your tee times, at any course, and those count in their stats too.
          </LI>
        </UL>
      </Step>

      <Step n={8} title="Stats" when="Any time (each player)">
        <P>
          Every scored round feeds the player&rsquo;s <strong>📊 Stats</strong> in his app. The more
          rounds scored hole by hole, the more it shows.
        </P>
        <PhoneShot
          src="/guides/scoring/stats.webp"
          alt="The Stats overview: scoring average 85.8 with the last 5 and last 10, best and worst rounds, putts, 3-putts, greens in regulation, handicap and scoring by par"
          caption="The Overview tab of a player&rsquo;s stats."
        />
        <H3>Overview</H3>
        <UL>
          <LI>
            <strong>Scoring average</strong>, with the average of the last 5 and last 10 rounds and
            whether he&rsquo;s trending up, down or steady.
          </LI>
          <LI>
            <strong>Best Round</strong> and <strong>Worst (Last 20)</strong>, with course and date.
          </LI>
          <LI>
            <strong>Putts / Round</strong> and <strong>3-Putts / Round</strong> (rated good, average or
            needs work).
          </LI>
          <LI>
            <strong>GIR / Round</strong>: greens in regulation, estimated from score and putts on holes
            where putts were entered.
          </LI>
          <LI>
            <strong>Handicap:</strong> his current index.
          </LI>
          <LI>
            <strong>Scoring by Par:</strong> his average on par 3s, par 4s and par 5s.
          </LI>
          <LI>
            <strong>Score Distribution:</strong> how often he makes eagle or better, birdie, par,
            bogey, double and triple or worse.
          </LI>
          <LI>
            <strong>Scoring Trend</strong> and <strong>Putting Trend</strong> charts over his recent
            rounds.
          </LI>
        </UL>
        <H3>Rounds</H3>
        <UL>
          <LI>Every round with date, course, score, putts and 3-putts.</LI>
        </UL>
        <H3>Courses</H3>
        <UL>
          <LI>
            Rounds, average and best at each course. Open a course for his average on every hole,
            with his <strong>Strongest Hole</strong> and <strong>Toughest Hole</strong>.
          </LI>
        </UL>
        <H3>Group</H3>
        <UL>
          <LI>
            <strong>Group Leaderboard:</strong> every player in your group with rounds played, gross
            and net scoring average, and best gross and net rounds.
          </LI>
          <LI>
            <strong>Head to Head:</strong> his record against each player he has played with: rounds
            together and wins, losses and ties, gross and net.
          </LI>
        </UL>
        <H3>Wall of Fame</H3>
        <UL>
          <LI>Your group&rsquo;s aces, eagles and par-or-better rounds.</LI>
        </UL>
        <Heads>
          Picked-up holes (&ldquo;7x&rdquo;) are left out of the hole-by-hole stats, so a pick-up
          doesn&rsquo;t skew a player&rsquo;s averages.
        </Heads>
      </Step>

      <H2>Games and tournaments</H2>
      <UL>
        <LI>
          A guide to <strong>games</strong>, such as skins and the putting game, is coming.
        </LI>
        <LI>
          Running a tournament? See the event setup guides for{' '}
          <InlineLink to="/guides/how-to-set-up-a-captains-choice-golf-tournament">
            Captain&rsquo;s Choice
          </InlineLink>{' '}
          and <InlineLink to="/guides/how-to-set-up-a-best-ball-golf-tournament">Best Ball</InlineLink>.
        </LI>
      </UL>

      <FAQ id="scoring-your-rounds" faqs={FAQS} />
      <div className="guide-no-print">
        <GuideCTA />
      </div>
    </article>
  );
}
